import { RoundedLogo } from '@assets/newlogo';
import DesktopAuthWrapper from '@components/atoms/DesktopAuthWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTitle } from '@hooks/useTitle';
import {
    ButtonLabel,
    InfoBox,
    InfoBoxVariant,
    InputField,
} from '@moreharvest/component-library';
import { login } from '@services/analytics';
import { AnalyticsAuthMethod, AnalyticsAuthStage } from 'mh-client';
import { useAppDispatch, useAppSelector } from '@store/index';
import {
    clearLoginPageError,
    selectLoginPageError,
} from '@store/reducers/errorMessages/loginPageErrors';
import { setEmail } from '@store/reducers/profile';
import { RE_EMAIL } from '@utils/constants';
import { FormattedMessage } from '@utils/i18n';
import { FieldValues, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router';
import { object, string } from 'yup';

export interface LoginInputs extends FieldValues {
    email: string;
}

const Login = () => {
    useTitle('account_login', 'enterEmail');
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const errorMessage = useAppSelector(selectLoginPageError);

    const schema = object().shape({
        email: string()
            .matches(
                RE_EMAIL,
                intl.formatMessage({ id: 'account_incorrect_email' })
            )
            .required(
                intl.formatMessage({
                    id: 'account_incorrect_email',
                })
            ),
    });
    const { register, handleSubmit, watch, formState, reset } =
        useForm<LoginInputs>({
            defaultValues: {
                email: '',
            },
            resolver: yupResolver(schema),
        });
    const { errors } = formState;
    const onSubmit = (data: LoginInputs) => {
        login({
            method: AnalyticsAuthMethod.Email,
            stage: AnalyticsAuthStage.Initiated,
        });
        dispatch(clearLoginPageError());
        dispatch(setEmail(data.email));
        navigate('/login/send-magic-link');
    };

    return (
        <DesktopAuthWrapper>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex
                flex-col
                flex-1
                w-full
                justify-center
                items-center my-auto gap-y-12"
            >
                <RoundedLogo size="small" />
                {errorMessage && (
                    <InfoBox
                        variant={InfoBoxVariant.error}
                        message={errorMessage}
                    />
                )}
                <div className="w-full flex flex-1 gap-y-8 flex-col items-center mx-auto">
                    <InputField
                        placeholder={intl.formatMessage({
                            id: 'email_address',
                        })}
                        id="login_input_emailAddress"
                        variant="default"
                        register={register('email')}
                        errorMessage={
                            errors['email']?.message
                                ? intl.formatMessage({
                                      id: 'account_incorrect_email',
                                  })
                                : undefined
                        }
                        showRightIcon={!!watch('email')}
                        closeIconClick={() => {
                            reset({
                                email: '',
                            });
                        }}
                    />
                    <div className="flex flex-col gap-y-2 w-full items-center">
                        <ButtonLabel
                            size="medium"
                            variant="solid"
                            type="submit"
                            id="login_CTA_logIn"
                        >
                            <FormattedMessage id="account_login" />
                        </ButtonLabel>
                        <Link to="/register" id="login_link_notRegistered">
                            <ButtonLabel
                                size="small"
                                variant="empty"
                                type="button"
                            >
                                <FormattedMessage id="login_not_registered" />
                            </ButtonLabel>
                        </Link>
                    </div>
                </div>
            </form>
        </DesktopAuthWrapper>
    );
};

export default Login;
